import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { BellIcon } from "@heroicons/react/24/outline";
import { Dropdown } from "../components/ui";
import { scholarshipsApi } from "../api/scholarships";
import { formatDateTime } from "../components/scholarships/review/model";

// The signed-in person's notifications (a student's invitation, a sponsor's new nominees): unread count on the bell, latest first.
// `scope` is "student" or "staff", the API each reads them from.
export default function NotificationBell({ scope = "student" }) {
  const NOTIFICATIONS_KEY = [scope, "notifications"];
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { data } = useQuery({
    queryKey: NOTIFICATIONS_KEY,
    refetchInterval: 5 * 60 * 1000,
    queryFn: async () => {
      try {
        return (await scholarshipsApi.get(`${scope}/notifications`)).data;
      } catch (error) {
        console.error("Failed to load notifications:", error);
        throw error;
      }
    },
  });
  const items = data?.items ?? [];
  const unread = data?.unread ?? 0;

  const markRead = async (path) => {
    try {
      await scholarshipsApi.post(path);
    } catch (error) {
      console.error(`Failed to mark notifications read (${path}):`, error);
    }
    queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
  };

  const open = (n) => {
    if (!n.readAt) markRead(`${scope}/notifications/${n.id}/read`);
    if (n.linkPath) navigate(n.linkPath);
  };

  return (
    <Dropdown
      label="Notifications"
      panelClassName="w-80"
      triggerClassName="relative rounded-md p-2 text-slate-600 hover:bg-slate-50"
      trigger={
        <>
          <BellIcon className="h-6 w-6" />
          {unread > 0 && (
            <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </>
      }
    >
      <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2">
        <span className="text-sm font-semibold text-slate-800">Notifications</span>
        {unread > 0 && (
          <button type="button" onClick={() => markRead(`${scope}/notifications/read-all`)} className="text-xs font-medium text-brand hover:underline">
            Mark all read
          </button>
        )}
      </div>
      {items.length === 0 ? (
        <p className="px-3 py-6 text-center text-sm text-slate-500">No notifications yet.</p>
      ) : (
        <ul className="max-h-96 overflow-y-auto">
          {items.map((n) => (
            <li key={n.id}>
              <button type="button" onClick={() => open(n)} className="flex w-full flex-col gap-0.5 px-3 py-2 text-left hover:bg-slate-50">
                <span className={`text-sm ${n.readAt ? "text-slate-700" : "font-semibold text-slate-900"}`}>{n.title}</span>
                <span className="text-xs text-slate-600">{n.body}</span>
                <span className="text-xs text-slate-400">{formatDateTime(n.createdAt)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </Dropdown>
  );
}
